import { inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { patchState } from '@ngrx/signals';
import { filter, Subscription } from 'rxjs';

/**
 * Creates a route-based state reset handler that can be used in any signal store
 * @param routePath The path segment to monitor (when navigating away from this path, state is reset)
 * @returns A function that can be called inside withMethods to set up state reset
 */
export function createStateResetHandler<T>(routePaths: string | string[]) {
  return (store: { [key: string]: any }, initialState: T): Subscription => {
    const router = inject(Router);
    const paths = Array.isArray(routePaths) ? routePaths : [routePaths];
    // Set up subscription to navigation events
    return router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        filter((event: NavigationEnd) => !paths.some(path => event.url.includes(path))),
      )
      .subscribe(() => {
        try {
          patchState(store as any, initialState as any);
        } catch (error) {
          console.error('Error while resetting state:', error);
        }
      });
  };
}

/**
 * Creates a route-based state reset handler that can be used in any signal store
 * @param routePath The path segment to monitor (when navigating to this specific path, state is reset)
 * @param state The partial state object to apply to store state
 * @returns A function that can be called inside withMethods to set up state reset
 */

export function createCustomStateSetOnUrlHandler<T>(routePaths: string | string[], state: Partial<T>) {
  return (store: { [key: string]: any }): Subscription => {
    const router = inject(Router);
    const paths = Array.isArray(routePaths) ? routePaths : [routePaths];
    // Set up subscription to navigation events
    return router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        filter((event: NavigationEnd) => paths.some(path => event.url === path)),
      )
      .subscribe(() => {
        try {
          patchState(store as any, state as any);
        } catch (error) {
          console.error('Error while resetting state:', error);
        }
      });
  };
}

/**
 * Creates a route-based state reset handler that can be used in any signal store when route matches a regex.
 * Ensure that you use ^ and $ to match the exact URL, unless matching a part of it is appropriate for your use case.
 * @param regex The regex to match against the URL (when navigating to this specific path, state is reset)
 * @returns A function that can be called inside withMethods to set up state reset
 */

export const guidRegex = '[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}';
export function createCustomStateSetOnRegexHandler<T>(regex: RegExp): (store: { [key: string]: any }, state: Partial<T>) => Subscription {
  return (store: { [key: string]: any }, state: Partial<T>): Subscription => {
    const router = inject(Router);
    // Set up subscription to navigation events
    return router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        filter((event: NavigationEnd) => regex.test(event.url)),
      )
      .subscribe(() => {
        try {
          patchState(store as any, state as any);
        } catch (error) {
          console.error('Error while resetting state:', error);
        }
      });
  };
}
